import {Controller} from "stimulus";
import {TextHighlighter} from "src/common/text_highlighter";

/**
 * Filters a list of items by matching text input against each item's text content.
 *
 * Values:
 * - matchHiddenText (Boolean, default: false) — When true, matches against textContent
 *   (includes text inside collapsed elements like <details>). When false, matches
 *   against innerText (only visible text).
 * - toggleClasses (Array, default: ["hidden"]) — CSS classes to add/remove on items
 *   when filtering. Use this to change the visual treatment of non-matching items,
 *   e.g. ["opacity-50"] to fade them instead of hiding.
 * - highlightName (String) — Name of the CSS custom
 *   highlight to register. Must be unique per page when multiple instances are present.
 *
 * Targets:
 * - highlight — Elements to search for matching text within. When present,
 *   enables text highlighting via the CSS Custom Highlight API.
 */
export default class FilterableListController extends Controller {
  static targets = ["item", "highlight"];
  static values = {
    matchHiddenText: {type: Boolean, default: false},
    toggleClasses: {type: Array, default: ["hidden"]},
    highlightName: {type: String},
  };

  declare readonly itemTargets: HTMLElement[];
  declare readonly matchHiddenTextValue: boolean;
  declare readonly toggleClassesValue: string[];
  declare readonly highlightNameValue: string;
  declare readonly highlightTargets: HTMLElement[];
  declare readonly hasHighlightTarget: boolean;

  private _highlighter: TextHighlighter | null = null;

  connect() {
    if (this.hasHighlightTarget) {
      this._highlighter = new TextHighlighter(this.highlightNameValue);
    }
  }

  disconnect() {
    this._highlighter?.clear();
    this._highlighter = null;
  }

  filter(event: Event) {
    if (event.target instanceof HTMLInputElement) {
      const value = event.target.value;
      if (value) {
        const searchTerms = this.buildSearchTerms(value);
        const elementsToShow = this.findElementsMatchingText(searchTerms);

        this.itemTargets.forEach((el) => this.hideItem(el));
        elementsToShow.forEach((el) => this.showItem(el));

        if (this._highlighter) {
          this._highlighter.highlight(searchTerms, this.highlightTargets);
        }
      } else {
        this.itemTargets.forEach((el) => this.showItem(el));
        this._highlighter?.clear();
      }

      this.dispatch("filter", {prefix: "tlw-filterable-list", bubbles: true, detail: {query: value}});
    }
  }

  findElementsMatchingText = (termsToMatch: RegExp[]) => {
    return this.itemTargets.filter((item) => {
      const text = this.matchHiddenTextValue ? item.textContent : item.innerText;
      return termsToMatch.every((termToMatch) => text?.match(termToMatch));
    });
  };

  private buildSearchTerms(text: string): RegExp[] {
    return text.trim().split(" ").map((s) => new RegExp(RegExp.escape(s), "i"));
  }

  private hideItem(el: HTMLElement) {
    el.classList.add(...this.toggleClassesValue);
  }

  private showItem(el: HTMLElement) {
    el.classList.remove(...this.toggleClassesValue);
  }
}
